/**
 * Service for managing exam progress persistence in localStorage
 * Uses email_exam_id as the key format for unique identification
 */

export const ExamPersistenceService = {
  /**
   * Generate storage key for exam progress
   * @param {string} email - User email
   * @param {string} examId - Exam ID
   * @returns {string} Storage key
   */
  getStorageKey: (email, examId) => `${email}_${examId}`,

  /**
   * Save exam progress to localStorage
   * @param {string} email - User email
   * @param {string} examId - Exam ID
   * @param {Object} progressData - Progress data to save
   */
  saveProgress: (email, examId, progressData) => {
    try {
      const key = ExamPersistenceService.getStorageKey(email, examId);
      const dataToSave = {
        ...progressData,
        lastSaved: Date.now(),
        version: "1.0"
      };
      localStorage.setItem(key, JSON.stringify(dataToSave));
      console.log(`Exam progress saved for ${email}_${examId}`);
    } catch (error) {
      console.error("Error saving exam progress:", error);
    }
  },

  /**
   * Load exam progress from localStorage
   * @param {string} email - User email
   * @param {string} examId - Exam ID
   * @returns {Object|null} Saved progress data or null
   */
  loadProgress: (email, examId) => {
    try {
      const key = ExamPersistenceService.getStorageKey(email, examId);
      const data = localStorage.getItem(key);
      if (data) {
        const parsedData = JSON.parse(data);
        console.log(`Exam progress loaded for ${email}_${examId}`);
        return parsedData;
      }
      return null;
    } catch (error) {
      console.error("Error loading exam progress:", error);
      return null;
    }
  },

  /**
   * Clear exam progress from localStorage
   * @param {string} email - User email
   * @param {string} examId - Exam ID
   */
  clearProgress: (email, examId) => {
    try {
      const key = ExamPersistenceService.getStorageKey(email, examId);
      localStorage.removeItem(key);
      console.log(`Exam progress cleared for ${email}_${examId}`);
    } catch (error) {
      console.error("Error clearing exam progress:", error);
    }
  },

  /**
   * Check if exam progress exists
   * @param {string} email - User email
   * @param {string} examId - Exam ID
   * @returns {boolean} True if progress exists
   */
  hasProgress: (email, examId) => {
    try {
      const key = ExamPersistenceService.getStorageKey(email, examId);
      return localStorage.getItem(key) !== null;
    } catch (error) {
      console.error("Error checking exam progress:", error);
      return false;
    }
  },

  /**
   * Get all exam progress for a user
   * @param {string} email - User email
   * @returns {Array} Array of exam progress entries
   */
  getAllUserProgress: (email) => {
    try {
      const userProgress = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(`${email}_`)) {
          const examId = key.replace(`${email}_`, '');
          const progress = ExamPersistenceService.loadProgress(email, examId);
          if (progress) {
            userProgress.push({ examId, progress });
          }
        }
      }
      return userProgress;
    } catch (error) {
      console.error("Error getting user progress:", error);
      return [];
    }
  },

  /**
   * Clean up old exam progress (older than 7 days)
   * @param {string} email - User email (optional, if not provided cleans all)
   */
  cleanupOldProgress: (email = null) => {
    try {
      const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
      const keysToRemove = [];

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (!email || key.startsWith(`${email}_`))) {
          try {
            const data = JSON.parse(localStorage.getItem(key));
            if (data.lastSaved && data.lastSaved < sevenDaysAgo) {
              keysToRemove.push(key);
            }
          } catch (e) {
            // If data is corrupted, remove it
            keysToRemove.push(key);
          }
        }
      }

      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log(`Cleaned up ${keysToRemove.length} old exam progress entries`);
    } catch (error) {
      console.error("Error cleaning up old progress:", error);
    }
  },

  /**
   * Validate saved progress data
   * @param {Object} progressData - Progress data to validate
   * @returns {boolean} True if data is valid
   */
  validateProgress: (progressData) => {
    if (!progressData || typeof progressData !== 'object') {
      return false;
    }

    const requiredFields = ['examId', 'userEmail', 'examStarted', 'timeLeft'];
    return requiredFields.every(field => progressData.hasOwnProperty(field));
  }
};

export default ExamPersistenceService;
